import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchData } from "../helper/api.interceptor";

const EditSpecification = () => {
    const navigate = useNavigate();
    const { productId } = useParams();
    const [specs, setSpecs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const fetchSpecs = async () => {
        setFetching(true);
        try {
            const res = await fetchData.get(`product/get-all-specification/${productId}`);

            if (res?.status_code === 200) {
                setSpecs((res.result || []).map(({ _id, feature_name, feature_dec }) => ({
                    _id,
                    feature_name: feature_name || "",
                    feature_dec: feature_dec || "",
                })));
            } else {
                Swal.fire("Error", res?.message || "Failed to fetch specifications", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong while fetching specifications", "error");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchSpecs();
    }, [productId]);

    const handleChange = (index, field, value) => {
        setSpecs((currentSpecs) => currentSpecs.map((spec, specIndex) => (
            specIndex === index ? { ...spec, [field]: value } : spec
        )));
    };

    const handleUpdateAll = async () => {
        if (!specs.length) {
            Swal.fire("Warning", "There are no specifications to update.", "warning");
            return;
        }

        for (const spec of specs) {
            if (!spec.feature_name.trim()) {
                Swal.fire("Warning", "Each specification needs a title.", "warning");
                return;
            }
        }

        setLoading(true);
        try {
            for (const spec of specs) {
                const result = await fetchData.patch(
                    `product/update-specification/${productId}/${spec._id}`,
                    {
                        feature_name: spec.feature_name.trim(),
                        feature_dec: spec.feature_dec,
                    }
                );

                if (result?.status_code !== 200) {
                    Swal.fire("Error", result?.message || "Failed to update specification", "error");
                    return;
                }
            }

            await fetchSpecs();
            Swal.fire("Success!", "Specifications updated successfully.", "success");
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Update failed", "error");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <p className="p-4">Loading specifications...</p>;

    if (!specs.length) {
        return (
            <div>
                <h6 className="fw-semibold mb-3">Edit Specifications</h6>
                <p className="text-muted">No specifications have been added for this product.</p>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-success"
                        onClick={() => navigate(`/product/add-specification/${productId}`)}
                    >
                        Add Specification
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate("/product_details")}>
                        Back to Products
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h6 className="fw-semibold mb-3">Edit Specifications</h6>

            {specs.map((spec, index) => (
                <div className="card mb-3 p-3" key={spec._id}>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Title</label>
                        <input
                            type="text"
                            className="form-control"
                            value={spec.feature_name}
                            onChange={(e) => handleChange(index, "feature_name", e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Description</label>
                        <textarea
                            className="form-control"
                            rows={2}
                            value={spec.feature_dec}
                            onChange={(e) => handleChange(index, "feature_dec", e.target.value)}
                        />
                    </div>
                </div>
            ))}

            <div className="d-flex gap-2 mb-4">
                <button className="btn btn-success" onClick={handleUpdateAll} disabled={loading}>
                    {loading ? "Updating..." : "Save Specifications"}
                </button>
            </div>

            <button className="btn btn-secondary" onClick={() => navigate("/product_details")}>
                Cancel
            </button>
        </div>
    );
};

export default EditSpecification;
